import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { CheckIcon } from '@heroicons/react/24/outline';
import { TanTable } from '../../components/TanTable';
import Notification, { NotificationContainer } from '../../components/Notification';
import { Drawer } from '../../components/ui/Drawer';
import EditHistoryLink from '../../components/tablemaint/EditHistoryLink';
import PermissionsContainer from '../../components/PermissionsContainer';
import { PermissionAwareInput, PermissionAwareButton } from '../../contexts/PermissionAccessContext';
import UserPicker from '../../components/UserPicker';
import TypeAhead from '../../components/TypeAhead';
import dispatchersService, { Dispatcher, DispatcherUpdate } from '../../services/dispatchers.service';
import permissionsService, { AzureADUser } from '../../services/permissions.service';
import { deletedRowClass } from '../../utils/deletedRow';
import api from '../../services/api';

// Allowed Preference values, in the casing we want to save with.
// Existing data may be uppercase (e.g. "LOCAL"); load is case-insensitive,
// save normalizes to this casing.
const PREFERENCE_OPTIONS = ['', 'Local', 'OTR', 'Both'] as const;

const normalizePreference = (raw: string): string => {
  if (!raw) return '';
  const lower = raw.trim().toLowerCase();
  return PREFERENCE_OPTIONS.find((opt) => opt.toLowerCase() === lower) ?? raw;
};

interface RegionOption {
  id: number;
  name: string;
}

type FormState = Omit<Dispatcher, 'recno'>;

const emptyForm: FormState = {
  username: '',
  displayname: '',
  gwi: false,
  region: '',
  preference: '',
  defaultdriver: '',
  revenueregion: '',
  dispatcheremail: '',
  doemail: '',
  radio: '',
  autoprint: true,
  autopregate: false,
  deleted: false,
  is_driver_dispatcher: false,
  is_order_dispatcher: false,
  preplan_fleet: true,
  azure_user_id: '',
};

// Diff form vs original to send only the changed fields. Keeps writes minimal
// and gives the backend's patch-style update something to work with.
function buildPatch(original: Dispatcher, current: FormState): DispatcherUpdate {
  const patch: DispatcherUpdate = {};
  (Object.keys(current) as (keyof FormState)[]).forEach((key) => {
    if (current[key] !== original[key]) {
      // @ts-expect-error key indexing across union
      patch[key] = current[key];
    }
  });
  return patch;
}

const BoolPill: React.FC<{ value: boolean }> = ({ value }) => (
  <span
    className={`inline-block px-2 py-0.5 text-xs font-medium rounded-full ${
      value
        ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
        : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
    }`}
  >
    {value ? 'Yes' : 'No'}
  </span>
);

const Toggle: React.FC<{
  label: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  hint?: string;
}> = ({ label, checked, onChange, disabled, hint }) => (
  <label className="flex items-start gap-3 cursor-pointer select-none py-1">
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      disabled={disabled}
      className="mt-0.5 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
    />
    <span className="text-sm">
      <span className="font-medium text-label">{label}</span>
      {hint && (
        <span className="block text-xs text-muted">{hint}</span>
      )}
    </span>
  </label>
);

const SelectField: React.FC<{
  label: string;
  value: string;
  onChange: (next: string) => void;
  options: { value: string; label: string }[];
  disabled?: boolean;
  required?: boolean;
  /** If the current value isn't in `options`, keep it visible and flag it
   *  (orange) rather than silently dropping legacy data from view. */
  preserveUnknown?: boolean;
}> = ({ label, value, onChange, options, disabled, required, preserveUnknown = true }) => {
  return (
    <div>
      <label className="block text-sm font-medium text-label mb-1">
        {label}
        {required && <span className="text-red-500"> *</span>}
      </label>
      <TypeAhead
        options={options}
        value={value}
        onChange={onChange}
        onClear={() => onChange('')}
        placeholder="Select…"
        disabled={disabled}
        showSearch
        flagInvalidValue={preserveUnknown}
      />
    </div>
  );
};

const TextField: React.FC<{
  label: string;
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
  required?: boolean;
  placeholder?: string;
  type?: string;
  maxLength?: number;
  invalid?: boolean;
}> = ({ label, value, onChange, disabled, required, placeholder, type = 'text', maxLength, invalid }) => (
  <div>
    <label className="block text-sm font-medium text-label mb-1">
      {label}
      {required && <span className="text-red-500"> *</span>}
    </label>
    <PermissionAwareInput
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      maxLength={maxLength}
      disabled={disabled}
      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 dark:bg-gray-700 dark:text-white ${
        invalid ? 'border-red-400 dark:border-red-500 focus:ring-red-500' : 'border-gray-300 dark:border-gray-600 focus:ring-blue-500'
      }`}
    />
  </div>
);

const Dispatchers: React.FC = () => {
  const [data, setData] = useState<Dispatcher[]>([]);
  const [loading, setLoading] = useState(true);
  const [includeDeleted, setIncludeDeleted] = useState(false);

  const [adUsers, setAdUsers] = useState<AzureADUser[]>([]);
  const adUsersByOid = useMemo(() => {
    const map = new Map<string, AzureADUser>();
    adUsers.forEach((u) => map.set(u.user_id, u));
    return map;
  }, [adUsers]);

  const [regions, setRegions] = useState<RegionOption[]>([]);

  const [editing, setEditing] = useState<Dispatcher | null>(null);
  const [isAdding, setIsAdding] = useState(false); // drawer open in create mode
  const [form, setForm] = useState<FormState>(emptyForm);
  const [isSaving, setIsSaving] = useState(false);
  // In-drawer errors (like the Locations drawer): a banner at the top + red field
  // highlights, revealed only after a blocked save. saveError holds an API failure.
  const [showErrors, setShowErrors] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const topRef = useRef<HTMLDivElement>(null);

  // Required-field validation for the drawer.
  const validation = useMemo(() => {
    const errors: string[] = [];
    const invalid = new Set<string>();
    if (!form.username.trim()) { errors.push('Username is required.'); invalid.add('username'); }
    if (!form.displayname.trim()) { errors.push('Display Name is required.'); invalid.add('displayname'); }
    return { errors, invalid };
  }, [form.username, form.displayname]);

  const bannerErrors = [...(showErrors ? validation.errors : []), ...(saveError ? [saveError] : [])];
  const fieldInvalid = (key: string) => showErrors && validation.invalid.has(key);

  // Scroll the drawer to the top so the banner is seen when a blocked save raises errors.
  useEffect(() => {
    if (showErrors || saveError) topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [showErrors, saveError]);

  const [errorNotification, setErrorNotification] = useState<{ title: string; message: string } | null>(null);
  const [successNotification, setSuccessNotification] = useState<{ title: string; message: string } | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await dispatchersService.list({ includeDeleted });
      setData(rows);
    } catch (err: any) {
      console.error('Error loading dispatchers:', err);
      setErrorNotification({
        title: 'Failed to Load Data',
        message: err?.response?.data?.detail || 'Could not load dispatchers from the server.',
      });
    } finally {
      setLoading(false);
    }
  }, [includeDeleted]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Pre-load AD users so we can render a display name in the azure_user_id column.
  // UserPicker has its own cache; this fetch primes its cache too.
  useEffect(() => {
    const controller = new AbortController();
    permissionsService
      .getEnabledUsers(controller.signal)
      .then(setAdUsers)
      .catch((err) => {
        if (err?.name === 'AbortError' || err?.code === 'ERR_CANCELED') return;
        console.error('Failed to load Azure AD users:', err);
      });
    return () => controller.abort();
  }, []);

  // Region/Revenue Region dropdown options come from safetynew.region.
  useEffect(() => {
    const controller = new AbortController();
    api
      .get<{ data: RegionOption[] }>('/safety/regions', { signal: controller.signal })
      .then((res) => setRegions(res.data.data || []))
      .catch((err) => {
        if (err?.name === 'AbortError' || err?.code === 'ERR_CANCELED') return;
        console.error('Failed to load regions:', err);
      });
    return () => controller.abort();
  }, []);

  const handleEditOpen = useCallback((row: Dispatcher) => {
    setEditing(row);
    setIsAdding(false);
    setShowErrors(false);
    setSaveError(null);
    const { recno, ...rest } = row;
    void recno;
    // Normalize preference casing on open so the dropdown selects the right
    // option even if existing data is uppercase. The diff against the original
    // row will then save the normalized value.
    setForm({ ...rest, preference: normalizePreference(rest.preference) });
  }, []);

  const handleAddOpen = useCallback(() => {
    setEditing(null);
    setForm(emptyForm);
    setShowErrors(false);
    setSaveError(null);
    setIsAdding(true);
  }, []);

  const handleEditClose = useCallback(() => {
    setEditing(null);
    setIsAdding(false);
    setForm(emptyForm);
    setShowErrors(false);
    setSaveError(null);
  }, []);

  const handleSave = useCallback(async () => {
    if (validation.errors.length) {
      setShowErrors(true);
      return;
    }
    setShowErrors(false);
    setSaveError(null);
    setIsSaving(true);
    try {
      if (editing) {
        const patch = buildPatch(editing, form);
        if (Object.keys(patch).length === 0) {
          handleEditClose();
          return;
        }
        const updated = await dispatchersService.update(editing.recno, patch);
        setData((prev) => prev.map((d) => (d.recno === updated.recno ? updated : d)));
        setSuccessNotification({ title: 'Saved', message: `${updated.displayname || updated.username} updated.` });
      } else {
        // Create mode — the soft-delete flag isn't offered on Add, so omit it.
        const { deleted, ...rest } = form;
        void deleted;
        const created = await dispatchersService.create({ ...rest, preference: normalizePreference(rest.preference) });
        setData((prev) => [...prev, created]);
        setSuccessNotification({ title: 'Created', message: `${created.displayname || created.username} added.` });
      }
      handleEditClose();
    } catch (err: any) {
      console.error('Error saving dispatcher:', err);
      setSaveError(err?.response?.data?.detail || err?.message || 'Unknown error occurred');
    } finally {
      setIsSaving(false);
    }
  }, [editing, form, validation, handleEditClose]);

  const columns = useMemo<ColumnDef<Dispatcher>[]>(
    () => [
      {
        accessorKey: 'displayname',
        header: 'Display Name',
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
        size: 200,
        cell: ({ row }) => (
          <span className="font-semibold text-blue-600 dark:text-blue-400">{row.original.displayname}</span>
        ),
      },
      {
        accessorKey: 'username',
        header: 'Username',
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
        size: 140,
      },
      {
        accessorKey: 'region',
        header: 'Region',
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
        size: 120,
      },
      {
        accessorKey: 'is_driver_dispatcher',
        header: 'Driver Dispatcher',
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
        size: 140,
        cell: ({ row }) => <BoolPill value={row.original.is_driver_dispatcher} />,
      },
      {
        accessorKey: 'is_order_dispatcher',
        header: 'Order Dispatcher',
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
        size: 140,
        cell: ({ row }) => <BoolPill value={row.original.is_order_dispatcher} />,
      },
      {
        accessorKey: 'preplan_fleet',
        header: 'Preplan Fleet',
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
        size: 130,
        cell: ({ row }) => <BoolPill value={row.original.preplan_fleet} />,
      },
      {
        accessorKey: 'gwi',
        header: 'GWI',
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
        size: 80,
        cell: ({ row }) => <BoolPill value={row.original.gwi} />,
      },
      {
        accessorKey: 'azure_user_id',
        header: 'Azure AD User',
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
        size: 220,
        cell: ({ row }) => {
          const oid = row.original.azure_user_id;
          if (!oid) return <span className="text-gray-400">—</span>;
          const u = adUsersByOid.get(oid);
          if (!u) return <code className="text-xs text-amber-700 dark:text-amber-300">{oid}</code>;
          return (
            <div className="text-sm">
              <div className="text-body">{u.display_name || u.email}</div>
              {u.display_name && u.email && (
                <div className="text-xs text-muted">{u.email}</div>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: 'dispatcheremail',
        header: 'Dispatcher Email',
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
        size: 220,
      },
      {
        accessorKey: 'deleted',
        header: 'Deleted',
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
        size: 90,
        cell: ({ row }) => <BoolPill value={row.original.deleted} />,
      },
    ],
    [adUsersByOid]
  );

  return (
    <div className="h-full w-full p-10 pb-24">
      <PermissionsContainer componentId="dispatchers-table" componentName="Dispatchers Table">
        <TanTable
          data={data}
          columns={columns}
          loading={loading}
          settingsKey="dispatchers-table"
          fieldName="dispatchers-table"
          searchPlaceholder="Search dispatchers..."
          cardTitle="Dispatchers"
          enableGrouping={false}
          enableColumnFilters={true}
          showDeletedToggle={true}
          showDeleted={includeDeleted}
          onShowDeletedChange={setIncludeDeleted}
          getRowClassName={(row, m) => deletedRowClass(row.deleted, m.zebra)}
          onEditFilters={handleAddOpen}
          showEditFiltersButton={true}
          editFiltersButtonIcon="plus"
          editFiltersButtonTooltip="Add Dispatcher"
          onRowClick={handleEditOpen}
        />
      </PermissionsContainer>

      <Drawer
        isOpen={editing !== null || isAdding}
        onClose={handleEditClose}
        title={editing ? `Edit Dispatcher — ${editing.displayname || editing.username}` : 'Add Dispatcher'}
        position="right"
        maxWidth="lg"
        footer={
          <div className="flex items-center justify-end gap-3">
            <button
              onClick={handleEditClose}
              disabled={isSaving}
              className="px-4 py-2 text-sm font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
            >
              Cancel
            </button>
            <PermissionAwareButton
              onClick={handleSave}
              disabled={isSaving}
              className="px-4 py-2 text-sm font-medium text-accent-fg bg-accent rounded-lg hover:bg-accent-hover disabled:opacity-50 inline-flex items-center gap-2"
            >
              {isSaving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <CheckIcon className="w-4 h-4" />
                  Save Changes
                </>
              )}
            </PermissionAwareButton>
          </div>
        }
      >
        <PermissionsContainer componentId="dispatchers-edit-form" componentName="Edit Dispatcher Form">
          <div className="space-y-6">
            <div ref={topRef} />
            {editing && (
              <div className="flex justify-end">
                <EditHistoryLink endpoint={`/dispatchers/${editing.recno}/history`} label={editing.displayname || editing.username} />
              </div>
            )}
            {bannerErrors.length > 0 && (
              <div className="rounded-lg border border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/30 p-3">
                <p className="text-sm font-semibold text-red-800 dark:text-red-300">
                  Please fix the following before saving:
                </p>
                <ul className="mt-1 list-disc list-inside text-sm text-red-700 dark:text-red-300">
                  {bannerErrors.map((e, i) => (<li key={i}>{e}</li>))}
                </ul>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <TextField
                label="Username"
                value={form.username}
                onChange={(v) => setForm((f) => ({ ...f, username: v }))}
                required
                maxLength={30}
                disabled={isSaving}
                placeholder="e.g. jdoe"
                invalid={fieldInvalid('username')}
              />
              <TextField
                label="Display Name"
                value={form.displayname}
                onChange={(v) => setForm((f) => ({ ...f, displayname: v }))}
                required
                maxLength={255}
                disabled={isSaving}
                invalid={fieldInvalid('displayname')}
              />
              <SelectField
                label="Region"
                value={form.region}
                onChange={(v) => setForm((f) => ({ ...f, region: v }))}
                options={[
                  { value: '', label: '— None —' },
                  ...regions.map((r) => ({ value: r.name, label: r.name })),
                ]}
                disabled={isSaving}
              />
              <SelectField
                label="Revenue Region"
                value={form.revenueregion}
                onChange={(v) => setForm((f) => ({ ...f, revenueregion: v }))}
                options={[
                  { value: '', label: '— None —' },
                  ...regions.map((r) => ({ value: r.name, label: r.name })),
                ]}
                disabled={isSaving}
              />
              <SelectField
                label="Preference"
                value={form.preference}
                onChange={(v) => setForm((f) => ({ ...f, preference: v }))}
                options={[
                  { value: '', label: '— None —' },
                  { value: 'Local', label: 'Local' },
                  { value: 'OTR', label: 'OTR' },
                  { value: 'Both', label: 'Both' },
                ]}
                disabled={isSaving}
              />
              <TextField
                label="SCAC"
                value={form.defaultdriver}
                onChange={(v) => setForm((f) => ({ ...f, defaultdriver: v }))}
                maxLength={80}
                disabled={isSaving}
              />
              <TextField
                label="Dispatcher Email"
                type="email"
                value={form.dispatcheremail}
                onChange={(v) => setForm((f) => ({ ...f, dispatcheremail: v }))}
                maxLength={255}
                disabled={isSaving}
              />
              <TextField
                label="DO Email"
                type="email"
                value={form.doemail}
                onChange={(v) => setForm((f) => ({ ...f, doemail: v }))}
                maxLength={255}
                disabled={isSaving}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-label mb-1">
                Linked Azure AD User
              </label>
              <p className="text-xs text-muted mb-2">
                Stored as the Azure AD object ID. Survives email/UPN changes.
              </p>
              <UserPicker
                value={form.azure_user_id}
                onChange={(oid) => setForm((f) => ({ ...f, azure_user_id: oid }))}
                disabled={isSaving}
                placeholder="Search AD users by name or email..."
              />
            </div>

            <fieldset className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <legend className="px-2 text-sm font-semibold text-label">Roles</legend>
              <Toggle
                label="Order Dispatcher"
                checked={form.is_order_dispatcher}
                onChange={(v) => setForm((f) => ({ ...f, is_order_dispatcher: v }))}
                disabled={isSaving}
                hint="Dispatches orders (appears in order-dispatcher filters)."
              />
              <Toggle
                label="Driver Dispatcher"
                checked={form.is_driver_dispatcher}
                onChange={(v) => setForm((f) => ({ ...f, is_driver_dispatcher: v }))}
                disabled={isSaving}
                hint="Dispatches drivers (appears in driver-dispatcher filters)."
              />
              <Toggle
                label="GWI"
                checked={form.gwi}
                onChange={(v) => setForm((f) => ({ ...f, gwi: v }))}
                disabled={isSaving}
                hint="Internal GWI dispatcher flag (legacy filter)."
              />
            </fieldset>

            <fieldset className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <legend className="px-2 text-sm font-semibold text-label">Settings</legend>
              <Toggle
                label="Auto Print"
                checked={form.autoprint}
                onChange={(v) => setForm((f) => ({ ...f, autoprint: v }))}
                disabled={isSaving}
              />
              <Toggle
                label="Auto Pre-Gate"
                checked={form.autopregate}
                onChange={(v) => setForm((f) => ({ ...f, autopregate: v }))}
                disabled={isSaving}
              />
              <Toggle
                label="Preplan Fleet"
                checked={form.preplan_fleet}
                onChange={(v) => setForm((f) => ({ ...f, preplan_fleet: v }))}
                disabled={isSaving}
                hint="Smart Preplan plans this dispatcher's drivers. Off leaves them all off the board."
              />
            </fieldset>

            {/* Status — soft-delete, matching the Locations drawer's Deleted box.
                Only shown when editing an existing dispatcher (not on Add). */}
            {editing && (
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
                <Toggle
                  label="Deleted"
                  checked={form.deleted}
                  onChange={(v) => setForm((f) => ({ ...f, deleted: v }))}
                  disabled={isSaving}
                />
                <p className="mt-1 ml-7 text-xs text-muted">
                  Soft-deletes the dispatcher — hidden from filters but preserved in history. Uncheck and save to restore.
                </p>
              </div>
            )}
          </div>
        </PermissionsContainer>
      </Drawer>

      {errorNotification && (
        <NotificationContainer position="top-right">
          <Notification
            type="error"
            title={errorNotification.title}
            message={errorNotification.message}
            showClose={true}
            onClose={() => setErrorNotification(null)}
            autoDismiss={5000}
          />
        </NotificationContainer>
      )}

      {successNotification && (
        <NotificationContainer position="top-right">
          <Notification
            type="success"
            title={successNotification.title}
            message={successNotification.message}
            showClose={true}
            onClose={() => setSuccessNotification(null)}
            autoDismiss={3000}
          />
        </NotificationContainer>
      )}
    </div>
  );
};

export default Dispatchers;
